import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Alert } from "reactstrap";
import AuthService from "../../../Services/auth/AuthService";
import StatBlock from "./StatBlock";
import FinanceTile from "./FinanceTile";

const asNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const BentoTile = ({ title, layout, children }) => (
  <article className={`dash-tile dash-tile--${layout}`}>
    <h2 className="dash-tile__title">{title}</h2>
    <div className="dash-tile__stats">{children}</div>
  </article>
);

const DashboardMetrics = () => {
  const {
    data: dashboardResponse,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["dashboardData"],
    queryFn: AuthService.getDashboardData,
  });

  const data = useMemo(() => {
    const raw = dashboardResponse?.success ? dashboardResponse.data : {};

    return {
      artists: asNumber(raw.artists_total),
      products: asNumber(raw.product_total),
      assets: asNumber(raw.assets_total),
      labels: asNumber(raw.label_total),
      ytLinked: asNumber(raw.artists_linked_youtube),
      ytUnlinked: asNumber(raw.artists_not_linked_youtube),
      contributors: asNumber(raw.contributor_total),
      splits: asNumber(raw.contributors_with_splits),
      linkedAssets: asNumber(raw.assets_with_contributors),
      bankReady: asNumber(raw.bank_info_completed),
      providers: asNumber(raw.provider_total),
      users: asNumber(raw.users_total),
      expenses: asNumber(raw.expenses_total),
      financeByCurrency: Array.isArray(raw.finance_by_currency) ? raw.finance_by_currency : [],
    };
  }, [dashboardResponse]);

  if (isError) {
    return (
      <Alert color="danger" className="dash-alert">
        Failed to load metrics: {error?.message || "Unknown error"}
      </Alert>
    );
  }

  return (
    <div className="dash-bento" aria-label="Dashboard metrics">
      <BentoTile title="Catalog" layout="catalog">
        <StatBlock label="Artists" value={data.artists} isLoading={isLoading} />
        <StatBlock label="Products" value={data.products} isLoading={isLoading} />
        <StatBlock label="Assets" value={data.assets} isLoading={isLoading} />
        <StatBlock label="Labels" value={data.labels} isLoading={isLoading} />
        <StatBlock label="YouTube linked" value={data.ytLinked} isLoading={isLoading} />
        <StatBlock label="YouTube unlinked" value={data.ytUnlinked} isLoading={isLoading} />
      </BentoTile>

      <BentoTile title="Platform" layout="platform">
        <StatBlock label="Users" value={data.users} isLoading={isLoading} />
        <StatBlock label="Expenses" value={data.expenses} isLoading={isLoading} />
      </BentoTile>

      <BentoTile title="Contributors" layout="contributors">
        <StatBlock label="Total" value={data.contributors} isLoading={isLoading} />
        <StatBlock label="With splits" value={data.splits} isLoading={isLoading} />
        <StatBlock label="Linked assets" value={data.linkedAssets} isLoading={isLoading} />
        <StatBlock label="Bank ready" value={data.bankReady} isLoading={isLoading} />
      </BentoTile>

      <FinanceTile
        financeByCurrency={data.financeByCurrency}
        providers={data.providers}
        isLoading={isLoading}
      />
    </div>
  );
};

export default DashboardMetrics;
